<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import GreetingInput from './GreetingInput.vue';
import ForwardInput from './ForwardInput.vue';

defineProps({
  inboxId: { type: Number, required: true },
});

const greeting = defineModel('greeting', { type: String, default: '' });
const greetingAudioUrl = defineModel('greetingAudioUrl', {
  type: String,
  default: '',
});
// An explicit undefined default stops Vue casting a missing value to false
const readOptionsSetting = defineModel('readOptions', {
  type: Boolean,
  default: undefined,
});
const options = defineModel('options', { type: Array, required: true });

const DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];
const ACTIONS = ['ring', 'voicemail', 'forward', 'info', 'repeat'];

const { t } = useI18n();

const actionOptions = ACTIONS.map(action => ({
  value: action,
  label: t(`INBOX_MGMT.CALL_HANDLING.MENU.ACTIONS.${action.toUpperCase()}`),
}));

const digitOptions = option =>
  DIGITS.filter(
    digit =>
      digit === option.digit ||
      !options.value.some(item => item.digit === digit)
  ).map(digit => ({ value: digit, label: digit }));

// On unless switched off, matching the server default (cached inbox data may predate the setting)
const readOptions = computed({
  get: () => readOptionsSetting.value !== false,
  set: value => {
    readOptionsSetting.value = value;
  },
});

const optionLines = () =>
  options.value.map(option =>
    option.action === 'repeat'
      ? t('INBOX_MGMT.CALL_HANDLING.MENU.PRESS_REPEAT', { digit: option.digit })
      : t('INBOX_MGMT.CALL_HANDLING.MENU.PRESS', {
          label: option.label,
          digit: option.digit,
        })
  );

// With "Read the options" off, the greeting is expected to say the options itself
const preview = computed(() =>
  [greeting.value, ...(readOptions.value ? optionLines() : [])].join(' ')
);

const addOption = () => {
  const digit = DIGITS.find(item => !options.value.some(o => o.digit === item));
  if (digit) options.value.push({ digit, label: '', action: 'ring' });
};

const removeOption = index => options.value.splice(index, 1);
</script>

<template>
  <div class="flex flex-col">
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.CALL_HANDLING.MENU.GREETING')"
      :help-text="
        greetingAudioUrl
          ? $t('INBOX_MGMT.CALL_HANDLING.MENU.RECORDED_PREVIEW')
          : $t('INBOX_MGMT.CALL_HANDLING.MENU.PREVIEW', { text: preview })
      "
    >
      <GreetingInput
        v-model:text="greeting"
        v-model:audio-url="greetingAudioUrl"
        :inbox-id="inboxId"
      />
      <label
        v-if="!greetingAudioUrl"
        class="flex items-center gap-2 mt-2 text-label-small text-n-slate-11"
      >
        <Switch v-model="readOptions" />
        {{ $t('INBOX_MGMT.CALL_HANDLING.MENU.READ_OPTIONS') }}
      </label>
    </SettingsFieldSection>
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.CALL_HANDLING.MENU.OPTIONS')"
      :help-text="$t('INBOX_MGMT.CALL_HANDLING.MENU.NO_CHOICE')"
    >
      <div class="flex flex-col gap-2">
        <div
          v-for="(option, index) in options"
          :key="option.digit"
          class="flex flex-col gap-2 p-3 rounded-xl outline outline-1 -outline-offset-1 outline-n-weak"
        >
          <div class="flex items-center gap-2">
            <Select
              v-model="option.digit"
              :options="digitOptions(option)"
              class="w-20 shrink-0"
            />
            <Input
              v-model="option.label"
              :placeholder="
                $t('INBOX_MGMT.CALL_HANDLING.MENU.LABEL_PLACEHOLDER')
              "
              class="flex-1 min-w-0"
            />
            <Select
              v-model="option.action"
              :options="actionOptions"
              class="w-48 shrink-0"
            />
            <Button
              size="sm"
              variant="ghost"
              color="slate"
              icon="i-lucide-trash-2"
              :aria-label="$t('INBOX_MGMT.CALL_HANDLING.MENU.REMOVE_OPTION')"
              @click="removeOption(index)"
            />
          </div>
          <ForwardInput
            v-if="option.action === 'forward'"
            v-model:number="option.forward_to"
            v-model:screen="option.forward_screen"
          />
          <GreetingInput
            v-else-if="option.action === 'info'"
            v-model:text="option.message"
            v-model:audio-url="option.message_audio_url"
            :inbox-id="inboxId"
            :placeholder="
              $t('INBOX_MGMT.CALL_HANDLING.MENU.MESSAGE_PLACEHOLDER')
            "
          />
          <p
            v-else-if="option.action === 'repeat'"
            class="mb-0 text-label-small text-n-slate-11"
          >
            {{ $t('INBOX_MGMT.CALL_HANDLING.MENU.REPEAT_HINT') }}
          </p>
          <p
            v-else-if="option.action === 'ring'"
            class="mb-0 text-label-small text-n-slate-11"
          >
            {{
              $t('INBOX_MGMT.CALL_HANDLING.MENU.RING_HINT', {
                label: option.label,
              })
            }}
          </p>
        </div>
        <div>
          <Button
            v-if="options.length < DIGITS.length"
            size="sm"
            variant="faded"
            color="slate"
            icon="i-lucide-plus"
            :label="$t('INBOX_MGMT.CALL_HANDLING.MENU.ADD_OPTION')"
            @click="addOption"
          />
        </div>
      </div>
    </SettingsFieldSection>
  </div>
</template>
